// ---------------------------------------------------------------------------
// Contract read-back — the deterministic half of S1 in
// docs/studio/wonbanking-studio-scenarios-2026-08-08.md.
//
// A bank developer's first input is usually a contract (an extracted server
// contract or an OpenAPI document), and the first thing Studio owes them is
// "here is what I read out of it" — before any generation, and without a
// model in the loop. Everything this module returns is computed by walking
// the pasted JSON; nothing is guessed. Two facts carry the trust the
// scenarios doc names:
//
//   - response fields the server never fills (`populatedByController: false`
//     in the extracted-contract dialect) — the developer must not discover
//     these after building the screen;
//   - points the contract itself refuses to decide (`"ambiguous": true`
//     blocks) — the questions Studio must ask rather than resolve silently.
//
// A text that does not parse as one of the two known dialects is prose, and
// the creation router treats it exactly as before — this module widens the
// intake, it never reinterprets existing prompts.
// ---------------------------------------------------------------------------

export interface ContractEndpointReadback {
  method: string
  path: string
  summary: string | null
}

export interface ContractUnfilledField {
  typeName: string
  fieldName: string
}

export interface ContractUnread {
  /**
   * Type / schema names whose field declaration this reader could NOT walk —
   * a schema composed with `allOf`/`$ref`, a type that only `extends`
   * another, a request/response naming a type the document does not define
   * here. Their fields are not in `declaredFieldNames`, so the card must say
   * so: a confident "N declared fields" over a document half of whose
   * declarations were skipped is the failure this list exists to prevent.
   */
  types: string[]
}

export interface ContractReadback {
  source: 'cogo-extracted-contract' | 'openapi'
  contractId: string | null
  endpoints: ContractEndpointReadback[]
  /**
   * DISTINCT field names the document declares, first-seen order. A name
   * declared once under `types` and again inline on an endpoint is ONE
   * declared field — the reader's walk is not the document's content.
   *
   * A NAME list, deliberately: it is not what `fieldCount` counts, and it is
   * not rendered as the card's number. See `fieldCount`.
   */
  declaredFieldNames: string[]
  /**
   * DECLARATIONS, deduplicated by owner+name — not `declaredFieldNames.length`.
   *
   * These are different claims and the card makes the second one ("{count}
   * declared fields"). `Customer`, `Account` and `Product` each declaring
   * {id, name, createdAt, updatedAt} is TWELVE declared fields; the size of
   * the name set is four, and reporting four for that document traded the
   * original double-count for an under-count with nothing disclosing it.
   * Deduplicating on owner+name is what removes the walk's repetition — the
   * response type read under `types` and again inline on the endpoint is one
   * declaration, seen twice — while keeping two same-named fields of two
   * different types as the two fields they are.
   */
  fieldCount: number
  /** Response fields declared in the DTO but never set by the server. */
  unfilledResponseFields: ContractUnfilledField[]
  /** Blocks the contract explicitly marks `ambiguous: true` — must-ask items. */
  ambiguousCount: number
  /** Declarations that were seen and not read — never silently dropped. */
  unread: ContractUnread
}

type JsonRecord = Record<string, unknown>

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Strips a single surrounding ``` fence (with an optional language tag). */
function unfence(text: string): string {
  const trimmed = text.trim()
  const match = /^```[A-Za-z0-9-]*\n([\s\S]*?)\n?```$/.exec(trimmed)
  return match ? match[1] : trimmed
}

function parseJsonObject(text: string): JsonRecord | null {
  const candidate = unfence(text)
  if (!candidate.startsWith('{')) return null
  try {
    const parsed: unknown = JSON.parse(candidate)
    return isRecord(parsed) ? parsed : null
  } catch {
    return null
  }
}

function fieldEntries(value: unknown): JsonRecord[] {
  if (!isRecord(value)) return []
  const fields = value.fields
  return Array.isArray(fields) ? fields.filter(isRecord) : []
}

/** True when this value carries a field list this reader can actually walk. */
function hasFieldList(value: unknown): boolean {
  return isRecord(value) && Array.isArray(value.fields)
}

/**
 * Collects what the document declares. The card's number is a statement ABOUT
 * THE DOCUMENT ("N declared fields"), so it has to be a property of the
 * document — not of how many times this reader happened to walk past a name.
 *
 * Two different things come out of it, because two different things are true:
 * `names` is the distinct set of names, and `declarationCount` counts
 * OWNER+NAME pairs. The owner is what separates "the walk saw this
 * declaration twice" (same owner — one field) from "the document declares two
 * fields that share a name" (different owners — two fields).
 */
class NameSet {
  private readonly seen = new Set<string>()
  private readonly declarations = new Set<string>()
  readonly names: string[] = []

  add(owner: string, value: unknown): void {
    if (typeof value !== 'string') return
    const name = value.trim()
    if (!name) return
    if (!this.seen.has(name)) {
      this.seen.add(name)
      this.names.push(name)
    }
    // NUL as the separator, not a space or a dot: a JSON key may legally
    // contain either, and then two different owner+name pairs could join to
    // the same string and be silently counted as one declaration.
    this.declarations.add(`${owner}\u0000${name}`)
  }

  get declarationCount(): number {
    return this.declarations.size
  }
}

/** A distinct, first-seen-order list of things that were seen and not read. */
class UnreadSet {
  private readonly seen = new Set<string>()
  readonly names: string[] = []

  add(name: string): void {
    if (!name || this.seen.has(name)) return
    this.seen.add(name)
    this.names.push(name)
  }
}

function countAmbiguous(value: unknown): number {
  if (Array.isArray(value)) {
    return value.reduce<number>((sum, entry) => sum + countAmbiguous(entry), 0)
  }
  if (!isRecord(value)) return 0
  let count = value.ambiguous === true ? 1 : 0
  for (const child of Object.values(value)) count += countAmbiguous(child)
  return count
}

function readExtractedContract(doc: JsonRecord): ContractReadback | null {
  if (!Array.isArray(doc.endpoints) || doc.endpoints.length === 0) return null
  const endpointRecords = doc.endpoints.filter(isRecord)
  if (endpointRecords.length !== doc.endpoints.length) return null

  const endpoints: ContractEndpointReadback[] = []
  for (const endpoint of endpointRecords) {
    // An "endpoints" array whose entries do not carry method+path is some
    // other document that happens to share the key — refuse the whole parse
    // rather than read back a partial truth.
    if (typeof endpoint.method !== 'string' || typeof endpoint.path !== 'string') {
      return null
    }
    endpoints.push({
      method: endpoint.method.toUpperCase(),
      path: endpoint.path,
      summary: typeof endpoint.summary === 'string' ? endpoint.summary : null,
    })
  }

  const declared = new NameSet()
  const unread = new UnreadSet()
  const unfilledResponseFields: ContractUnfilledField[] = []

  const types = isRecord(doc.types) ? doc.types : {}
  for (const [typeName, type] of Object.entries(types)) {
    if (!hasFieldList(type)) {
      // A type that only `extends`, `$ref`s or otherwise names its fields
      // elsewhere: seen, not read. Saying nothing here is how a document
      // whose types are all composed reads back as "0 declared fields".
      unread.add(typeName)
      continue
    }
    for (const field of fieldEntries(type)) declared.add(typeName, field.name)
  }

  /**
   * A request/response block usually either carries its own `fields` or names
   * a `type` that `doc.types` declares. Only the third case — neither — is
   * something this reader failed to read.
   */
  function readBlock(block: unknown, fallbackName: string): JsonRecord[] {
    if (hasFieldList(block)) {
      const entries = fieldEntries(block)
      // The OWNER of an inline block's fields is the type it names — that is
      // what makes `LoanLimitResponse.limitAmt` here the SAME declaration as
      // the one already read under `types`, rather than a second one.
      const owner =
        isRecord(block) && typeof block.type === 'string'
          ? block.type
          : fallbackName
      for (const field of entries) declared.add(owner, field.name)
      return entries
    }
    if (!isRecord(block)) return []
    const named =
      typeof block.type === 'string' && hasFieldList(types[block.type])
    if (!named) {
      unread.add(
        typeof block.type === 'string' ? block.type : fallbackName,
      )
    }
    return []
  }

  for (const endpoint of endpointRecords) {
    const where =
      typeof endpoint.id === 'string'
        ? endpoint.id
        : `${String(endpoint.method).toUpperCase()} ${String(endpoint.path)}`
    readBlock(endpoint.request, `${where} request`)
    const response = isRecord(endpoint.response) ? endpoint.response : null
    const responseFields = readBlock(endpoint.response, `${where} response`)
    const typeName =
      response && typeof response.type === 'string' ? response.type : where
    for (const field of responseFields) {
      if (field.populatedByController === false && typeof field.name === 'string') {
        unfilledResponseFields.push({ typeName, fieldName: field.name })
      }
    }
  }

  return {
    source: 'cogo-extracted-contract',
    contractId: typeof doc.contractId === 'string' ? doc.contractId : null,
    endpoints,
    declaredFieldNames: declared.names,
    fieldCount: declared.declarationCount,
    unfilledResponseFields,
    ambiguousCount: countAmbiguous(doc),
    unread: { types: unread.names },
  }
}

const OPENAPI_METHODS = ['get', 'post', 'put', 'delete', 'patch'] as const

/**
 * Whether a schema declares named fields SOMEWHERE THIS READER DOES NOT GO —
 * i.e. by composition. That is the only case where "not read" is true of the
 * schema rather than of this reader's expectations: a scalar, an enum and an
 * array declare no named fields at all, so there is nothing about them left
 * unread.
 */
function declaresComposedFields(schema: unknown): boolean {
  if (!isRecord(schema)) return false
  return (
    Array.isArray(schema.allOf) ||
    Array.isArray(schema.oneOf) ||
    Array.isArray(schema.anyOf) ||
    typeof schema.$ref === 'string'
  )
}

function readOpenApi(doc: JsonRecord): ContractReadback | null {
  const isOpenApi =
    typeof doc.openapi === 'string' || typeof doc.swagger === 'string'
  if (!isOpenApi || !isRecord(doc.paths)) return null

  const declared = new NameSet()
  const unread = new UnreadSet()

  /** `parameters: [{ name, in }]` — a parameter IS a declared field. */
  function readParameters(owner: string, value: unknown): void {
    if (!Array.isArray(value)) return
    for (const parameter of value) {
      if (isRecord(parameter)) declared.add(owner, parameter.name)
    }
  }

  const endpoints: ContractEndpointReadback[] = []
  for (const [path, item] of Object.entries(doc.paths)) {
    if (!isRecord(item)) continue
    // Path-level parameters apply to every operation under the path, so the
    // path itself owns them — read once, counted once.
    readParameters(path, item.parameters)
    for (const method of OPENAPI_METHODS) {
      const operation = item[method]
      if (operation === undefined) continue
      endpoints.push({
        method: method.toUpperCase(),
        path,
        summary:
          isRecord(operation) && typeof operation.summary === 'string'
            ? operation.summary
            : null,
      })
      if (isRecord(operation)) {
        readParameters(`${method.toUpperCase()} ${path}`, operation.parameters)
      }
    }
  }
  if (endpoints.length === 0) return null

  const components = isRecord(doc.components) ? doc.components : null
  const schemas = components && isRecord(components.schemas) ? components.schemas : null
  if (schemas) {
    for (const [schemaName, schema] of Object.entries(schemas)) {
      if (isRecord(schema) && isRecord(schema.properties)) {
        for (const property of Object.keys(schema.properties)) {
          declared.add(schemaName, property)
        }
        continue
      }
      // `allOf` / `oneOf` / `anyOf` / `$ref` composition: the schema DOES
      // declare fields and this reader does not resolve them. Named rather
      // than skipped, so the count is never mistaken for the whole content.
      //
      // Everything else here declares no named fields at all — an `enum` of
      // string values, an `array` of some element type, a bare `type: object`
      // with no properties. Nothing about those went unread, and reporting
      // them made the card claim declared types were skipped on nearly every
      // real OpenAPI document.
      if (declaresComposedFields(schema)) unread.add(schemaName)
    }
  }

  return {
    source: 'openapi',
    contractId:
      isRecord(doc.info) && typeof doc.info.title === 'string'
        ? doc.info.title
        : null,
    endpoints,
    declaredFieldNames: declared.names,
    fieldCount: declared.declarationCount,
    // OpenAPI has no populated-by marker; an empty list here means "none
    // declared", not "none exist" — the card copy states only what was read.
    unfilledResponseFields: [],
    ambiguousCount: countAmbiguous(doc),
    unread: { types: unread.names },
  }
}

export function parseContractReadback(text: string): ContractReadback | null {
  const doc = parseJsonObject(text)
  if (!doc) return null
  return readExtractedContract(doc) ?? readOpenApi(doc)
}
